import { useEffect } from 'react';
import { useOrganization } from '@/context/organization-context';
import { useNavigate, createFileRoute } from '@tanstack/react-router';

import { paths } from 'src/routes/paths';

import { useAuth } from 'src/lib/store/auth-store';

import { SplashScreen } from 'src/components/loading-screen';

/**
 * Bare /dashboard is tenant-less. Resolve the user's default org + client (from
 * context, seeded by the last-used localStorage value) and forward to the
 * tenant home. The real home content lives at the tenant-prefixed index.
 */
export const Route = createFileRoute('/_authed/dashboard/')({
  component: DashboardResolver,
});

function DashboardResolver() {
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const { activeOrganization, activeClient } = useOrganization();

  // A NexMail employee has no organization of their own, so this resolver would
  // spin on the splash while the org context retried an empty list and then
  // showed a "no organizations found" error. Their product is the queue
  // (WG-84), so that is where the bare path goes. Deliberately keyed on the
  // staff user type alone: platform admins are `admin` and keep the normal
  // dashboard, and no customer can reach this branch.
  useEffect(() => {
    if (isStaff) {
      navigate({ to: paths.w.queue, replace: true });
    }
  }, [isStaff, navigate]);

  useEffect(() => {
    if (isStaff) return;
    if (activeOrganization?.id && activeClient?.id) {
      // Preserve any query/hash (e.g. OAuth return params landing on /dashboard)
      // so the forward to the tenant home doesn't drop them.
      const qs =
        typeof window !== 'undefined'
          ? window.location.search + window.location.hash
          : '';
      const home = paths.dashboard.tenant(
        activeOrganization.id,
        activeClient.id,
      ).home;
      navigate({ to: `${home}${qs}`, replace: true });
    }
  }, [isStaff, activeOrganization?.id, activeClient?.id, navigate]);

  return <SplashScreen />;
}
